import { useInvitations } from '@/utils/hooks/invitations'
import {
  Typography,
  List,
  ListItem,
  Box,
  Divider,
  Button,
  Chip,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

import { useState } from 'react'
import { localisedDate, parseUTCDate } from '@/utils/dateUtils'

interface Invitation {
  invitation_uuid: string
  invitee_email: string | null
  status: string
  created_at: Date
  accepted_by_email: string | null
}

interface OrgUsersListProps {
  invitations: Invitation[]
  token: string | null
  orgUuid: string
  isMobile: boolean
}

const getExpiryDate = (createdAt: Date | string) => {
  const utcDate = parseUTCDate(createdAt)
  const expiryDate = new Date(utcDate)
  expiryDate.setUTCDate(expiryDate.getUTCDate() + 14)
  return expiryDate
}

const isInvitationExpired = (
  invitation: Invitation
): boolean => {
  if (invitation.status !== 'pending' || !invitation.created_at) {
    return false
  }
  const expiryDate = getExpiryDate(invitation.created_at)
  const now = new Date()
  return expiryDate.getTime() < now.getTime()
}

const getExpiryText = (createdAt: Date | string) => {
  const expiryDate = getExpiryDate(createdAt)
  const now = new Date()
  const daysUntilExpiry = Math.ceil(
    (expiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
  )

  if (daysUntilExpiry < 0) {
    return 'Expired'
  } else if (daysUntilExpiry === 0) {
    return 'Expires today'
  } else if (daysUntilExpiry === 1) {
    return 'Expires in 1 day'
  } else {
    return `Expires in ${daysUntilExpiry} days`
  }
}

export default function OrgUsersInvitationsList({
  invitations,
  token,
  orgUuid,
  isMobile,
}: OrgUsersListProps) {
  const { deleteInvitation } = useInvitations(token, '')
  const [deleteInviteError, setDeleteInviteError] = useState<string[]>([])
  const [deleting, setDeleting] = useState<boolean>(false)
  const [copiedInvitationId, setCopiedInvitationId] = useState<string | null>(
    null
  )

  const handleCopyLink = (invitationUuid: string) => {
    const invitationLink = `${window.location.origin}/invite/${invitationUuid}`
    navigator.clipboard.writeText(invitationLink)
    setCopiedInvitationId(invitationUuid)
    setTimeout(() => setCopiedInvitationId(null), 2000)
  }

  // Split invitations into email-based and link-based, and sort by status (pending first)
  const sortByStatus = (a: Invitation, b: Invitation) => {
    if (a.status === 'pending' && b.status !== 'pending') return -1
    if (a.status !== 'pending' && b.status === 'pending') return 1
    return 0
  }

  const emailInvitations = invitations
    .filter((inv) => inv.invitee_email)
    .sort(sortByStatus)
  const linkInvitations = invitations
    .filter((inv) => !inv.invitee_email)
    .sort(sortByStatus)

  async function handleDeleteInvitation(invitationUuid: string) {
    if (!token) {
      return
    }
    setDeleting(true)
    try {
      await deleteInvitation.mutateAsync({
        token,
        orgUuid,
        invitationUuid,
      })
    } catch {
      setDeleteInviteError([...deleteInviteError, invitationUuid])
    }
    setDeleting(false)
  }

  return (
    <>
      {/* Email Invitations */}
      {emailInvitations.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{
              mt: 4,
              mb: 1,
              ml: 2,
              width: '100%',
              fontSize: '24px',
            }}
          >
            Email Invitations
          </Typography>
          {isMobile ? (
            <List>
              {emailInvitations.map((invitation: Invitation) => (
                <Box key={invitation.invitation_uuid}>
                  <ListItem>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {invitation.invitee_email}
                    </Typography>
                  </ListItem>
                  <ListItem>
                    <Chip
                      label={
                        invitation.status === 'pending' &&
                        invitation.created_at &&
                        isInvitationExpired(invitation)
                          ? 'expired'
                          : invitation.status
                      }
                      variant="outlined"
                      color={
                        invitation.status === 'accepted'
                          ? 'success'
                          : invitation.status === 'declined'
                          ? 'error'
                          : invitation.status === 'pending' &&
                            invitation.created_at &&
                            isInvitationExpired(invitation)
                          ? 'default'
                          : 'warning'
                      }
                      sx={{
                        fontWeight: 500,
                        textAlign: 'center',
                        alignSelf: 'center',
                        mr: 2,
                      }}
                    />
                    Sent:{' '}
                    {invitation.created_at &&
                      localisedDate(new Date(invitation.created_at), 'dd MMMM yyyy')}
                  </ListItem>
                  {invitation.status === 'pending' && invitation.created_at && (
                    <ListItem>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {getExpiryText(new Date(invitation.created_at))}
                      </Typography>
                    </ListItem>
                  )}
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}
                  >
                    {deleteInviteError.find(
                      (id) => id === invitation.invitation_uuid
                    ) && (
                      <Alert severity="error">
                        Failed to cancel invitation
                      </Alert>
                    )}

                    {invitation.status === 'pending' && (
                      <Button
                        variant="outlined"
                        disableRipple
                        disabled={deleting}
                        sx={{
                          borderColor: '#1C3045',
                          color: '#1C3045',
                          borderRadius: 2,
                          borderWidth: 2,
                          fontWeight: 500,
                          textTransform: 'none',
                          '&:focus': {
                            backgroundColor: '#e8e8e8',
                          },
                        }}
                        onClick={() =>
                          handleDeleteInvitation(invitation.invitation_uuid)
                        }
                      >
                        {deleting
                          ? isInvitationExpired(invitation)
                            ? 'Deleting...'
                            : 'Cancelling...'
                          : isInvitationExpired(invitation)
                          ? 'Delete Invitation'
                          : 'Cancel Invitation'}
                      </Button>
                    )}
                  </Box>

                  <Divider />
                </Box>
              ))}
            </List>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        textAlign: 'left',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '30%',
                      }}
                    >
                      Recipient Email
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '25%',
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '20%',
                      }}
                    >
                      Sent Date
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '25%',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailInvitations.map((invitation: Invitation) => (
                    <TableRow key={invitation.invitation_uuid}>
                      <TableCell
                        sx={{
                          textAlign: 'left',
                          fontSize: '16px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          maxWidth: '200px',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        {invitation.invitee_email}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        <Chip
                          label={
                            invitation.status === 'pending' &&
                            invitation.created_at &&
                            isInvitationExpired(invitation)
                              ? 'expired'
                              : invitation.status
                          }
                          variant="outlined"
                          color={
                            invitation.status === 'accepted'
                              ? 'success'
                              : invitation.status === 'declined'
                              ? 'error'
                              : invitation.status === 'pending' &&
                                invitation.created_at &&
                                isInvitationExpired(invitation)
                              ? 'default'
                              : 'warning'
                          }
                          sx={{
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        {invitation.created_at &&
                          localisedDate(new Date(invitation.created_at), 'dd MMMM yyyy')}
                        {invitation.status === 'pending' &&
                          invitation.created_at && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              {getExpiryText(new Date(invitation.created_at))}
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'right',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        <Box>
                          {deleteInviteError.find(
                            (id) => id === invitation.invitation_uuid
                          ) && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              Failed to cancel invitation
                            </Alert>
                          )}

                          {invitation.status === 'pending' && (
                            <Button
                              variant="outlined"
                              disableRipple
                              disabled={deleting}
                              sx={{
                                borderColor: '#1C3045',
                                color: '#1C3045',
                                borderRadius: 2,
                                borderWidth: 2,
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:focus': {
                                  backgroundColor: '#e8e8e8',
                                },
                              }}
                              onClick={() =>
                                handleDeleteInvitation(
                                  invitation.invitation_uuid
                                )
                              }
                            >
                              {deleting
                          ? isInvitationExpired(invitation)
                            ? 'Deleting...'
                            : 'Cancelling...'
                          : isInvitationExpired(invitation)
                          ? 'Delete Invitation'
                          : 'Cancel Invitation'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* Link Invitations */}
      {linkInvitations.length > 0 && (
        <>
          <Typography
            variant="h6"
            sx={{
              mt: 4,
              mb: 1,
              ml: 2,
              width: '100%',
              fontSize: '24px',
            }}
          >
            Invitation Links
          </Typography>
          {isMobile ? (
            <List>
              {linkInvitations.map((invitation: Invitation) => (
                <Box key={invitation.invitation_uuid}>
                  <ListItem>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ flex: 1, wordBreak: 'break-all', mr: 2, mt: 1 }}
                      >
                        {`${window.location.origin}/invite/${invitation.invitation_uuid}`}
                      </Typography>
                      <Tooltip
                        title={
                          invitation.status !== 'pending'
                            ? 'Link no longer valid'
                            : copiedInvitationId === invitation.invitation_uuid
                            ? 'Copied!'
                            : 'Copy link'
                        }
                        placement="bottom"
                      >
                        <span>
                          <IconButton
                            onClick={() =>
                              handleCopyLink(invitation.invitation_uuid)
                            }
                            disabled={invitation.status !== 'pending'}
                            disableRipple
                            size="small"
                            sx={{
                              color:
                                copiedInvitationId === invitation.invitation_uuid
                                  ? '#4caf50'
                                  : '#1C3045',
                              '&:hover': {
                                color: 'primary.main',
                              },
                              '&:focus': {
                                backgroundColor: '#e8e8e8',
                              },
                            }}
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </ListItem>
                  <ListItem>
                    <Chip
                      label={
                        invitation.status === 'pending' &&
                        invitation.created_at &&
                        isInvitationExpired(invitation)
                          ? 'expired'
                          : invitation.status
                      }
                      variant="outlined"
                      color={
                        invitation.status === 'accepted'
                          ? 'success'
                          : invitation.status === 'declined'
                          ? 'error'
                          : invitation.status === 'pending' &&
                            invitation.created_at &&
                            isInvitationExpired(invitation)
                          ? 'default'
                          : 'warning'
                      }
                      sx={{
                        fontWeight: 500,
                        textAlign: 'center',
                        alignSelf: 'center',
                        mr: 2,
                      }}
                    />
                    Sent:{' '}
                    {invitation.created_at &&
                      localisedDate(new Date(invitation.created_at), 'dd MMMM yyyy')}
                  </ListItem>
                  {invitation.status === 'accepted' &&
                    invitation.accepted_by_email && (
                      <ListItem>
                        <Typography
                          variant="body2"
                          sx={{ color: 'text.secondary', fontStyle: 'italic' }}
                        >
                          Accepted by: {invitation.accepted_by_email}
                        </Typography>
                      </ListItem>
                    )}
                  {invitation.status === 'pending' && invitation.created_at && (
                    <ListItem>
                      <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary' }}
                      >
                        {getExpiryText(new Date(invitation.created_at))}
                      </Typography>
                    </ListItem>
                  )}
                  <Box
                    sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}
                  >
                    {deleteInviteError.find(
                      (id) => id === invitation.invitation_uuid
                    ) && (
                      <Alert severity="error">
                        Failed to cancel invitation
                      </Alert>
                    )}

                    {invitation.status === 'pending' && (
                      <Button
                        variant="outlined"
                        disableRipple
                        disabled={deleting}
                        sx={{
                          borderColor: '#1C3045',
                          color: '#1C3045',
                          borderRadius: 2,
                          borderWidth: 2,
                          fontWeight: 500,
                          textTransform: 'none',
                          '&:focus': {
                            backgroundColor: '#e8e8e8',
                          },
                        }}
                        onClick={() =>
                          handleDeleteInvitation(invitation.invitation_uuid)
                        }
                      >
                        {deleting
                          ? isInvitationExpired(invitation)
                            ? 'Deleting...'
                            : 'Cancelling...'
                          : isInvitationExpired(invitation)
                          ? 'Delete Invitation'
                          : 'Cancel Invitation'}
                      </Button>
                    )}
                  </Box>

                  <Divider />
                </Box>
              ))}
            </List>
          ) : (
            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        textAlign: 'left',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '30%',
                      }}
                    >
                      Invitation Link
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '25%',
                      }}
                    >
                      Status
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'center',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '20%',
                      }}
                    >
                      Created Date
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                        fontSize: '18px',
                        fontWeight: 'normal',
                        borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        width: '25%',
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkInvitations.map((invitation: Invitation) => (
                    <TableRow key={invitation.invitation_uuid}>
                      <TableCell
                        sx={{
                          textAlign: 'left',
                          fontSize: '16px',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              wordBreak: 'break-all',
                              flex: 1,
                              mr: 1,
                            }}
                          >
                            {`${window.location.origin}/invite/${invitation.invitation_uuid}`}
                          </Typography>
                          <Tooltip
                            title={
                              invitation.status !== 'pending'
                                ? 'Link no longer valid'
                                : copiedInvitationId === invitation.invitation_uuid
                                ? 'Copied!'
                                : 'Copy link'
                            }
                            placement="bottom"
                          >
                            <span>
                              <IconButton
                                onClick={() =>
                                  handleCopyLink(invitation.invitation_uuid)
                                }
                                disabled={invitation.status !== 'pending'}
                                disableRipple
                                size="small"
                                sx={{
                                  color:
                                    copiedInvitationId ===
                                    invitation.invitation_uuid
                                      ? '#4caf50'
                                      : '#1C3045',
                                  '&:hover': {
                                    color: 'primary.main',
                                  },
                                  '&:focus': {
                                    backgroundColor: '#e8e8e8',
                                  },
                                }}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        <Chip
                          label={
                            invitation.status === 'pending' &&
                            invitation.created_at &&
                            isInvitationExpired(invitation)
                              ? 'expired'
                              : invitation.status
                          }
                          variant="outlined"
                          color={
                            invitation.status === 'accepted'
                              ? 'success'
                              : invitation.status === 'declined'
                              ? 'error'
                              : invitation.status === 'pending' &&
                                invitation.created_at &&
                                isInvitationExpired(invitation)
                              ? 'default'
                              : 'warning'
                          }
                          sx={{
                            fontWeight: 500,
                          }}
                        />
                        {invitation.status === 'accepted' &&
                          invitation.accepted_by_email && (
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                mt: 0.5,
                                fontStyle: 'italic',
                                display: 'block',
                              }}
                            >
                              Accepted by: {invitation.accepted_by_email}
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'center',
                          fontSize: '16px',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        {invitation.created_at &&
                          localisedDate(new Date(invitation.created_at), 'dd MMMM yyyy')}
                        {invitation.status === 'pending' &&
                          invitation.created_at && (
                            <Typography
                              variant="body2"
                              sx={{ color: 'text.secondary', mt: 0.5 }}
                            >
                              {getExpiryText(new Date(invitation.created_at))}
                            </Typography>
                          )}
                      </TableCell>
                      <TableCell
                        sx={{
                          textAlign: 'right',
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                        }}
                      >
                        <Box>
                          {deleteInviteError.find(
                            (id) => id === invitation.invitation_uuid
                          ) && (
                            <Alert severity="error" sx={{ mb: 1 }}>
                              Failed to cancel invitation
                            </Alert>
                          )}

                          {invitation.status === 'pending' && (
                            <Button
                              variant="outlined"
                              disableRipple
                              disabled={deleting}
                              sx={{
                                borderColor: '#1C3045',
                                color: '#1C3045',
                                borderRadius: 2,
                                borderWidth: 2,
                                fontWeight: 500,
                                textTransform: 'none',
                                '&:focus': {
                                  backgroundColor: '#e8e8e8',
                                },
                              }}
                              onClick={() =>
                                handleDeleteInvitation(
                                  invitation.invitation_uuid
                                )
                              }
                            >
                              {deleting
                          ? isInvitationExpired(invitation)
                            ? 'Deleting...'
                            : 'Cancelling...'
                          : isInvitationExpired(invitation)
                          ? 'Delete Invitation'
                          : 'Cancel Invitation'}
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}
    </>
  )
}
